import React, { useState, useEffect } from 'react';
import { Music, Pause, Play } from 'lucide-react';
import WidgetContainer from '../WidgetContainer';

const NowPlaying = ({ delay = 0 }) => {
  const [spotifyData, setSpotifyData] = useState(null);
  const [localProgress, setLocalProgress] = useState(0);
  const [lastFetchTime, setLastFetchTime] = useState(Date.now());

  // Fetch Spotify data every 2 seconds
  useEffect(() => {
    const fetchSpotifyData = async () => {
      try {
        const response = await fetch('https://spotify.sillydev.lol/current');
        const data = await response.json();
        
        // Check if there's an actual song playing
        if (data.playing && data.track_name) {
          setSpotifyData(data);
          setLocalProgress(data.progress_ms);
          setLastFetchTime(Date.now());
        } else {
          // Set offline state
          setSpotifyData({ playing: false, offline: true });
        }
      } catch (error) {
        console.error('Failed to fetch Spotify data:', error);
        // Set offline state on error
        setSpotifyData({ playing: false, offline: true });
      }
    };

    fetchSpotifyData();
    const fetchInterval = setInterval(fetchSpotifyData, 2000);
    return () => clearInterval(fetchInterval);
  }, []);

  // Update progress locally every 100ms for smooth animation
  useEffect(() => {
    if (!spotifyData || !spotifyData.playing) return;

    const progressInterval = setInterval(() => {
      const elapsedSinceFetch = Date.now() - lastFetchTime;
      const newProgress = localProgress + elapsedSinceFetch;
      
      if (newProgress < spotifyData.duration_ms) {
        setLocalProgress(localProgress + 100);
      }
    }, 100);

    return () => clearInterval(progressInterval);
  }, [spotifyData, localProgress, lastFetchTime]);

  if (!spotifyData) {
    return (
      <WidgetContainer size="1x2" delay={delay}>
        <div className="flex items-center justify-center h-full">
          <Music className="w-6 h-6 text-coffee-300 animate-pulse" />
        </div>
      </WidgetContainer>
    );
  }

  // Show offline state
  if (spotifyData.offline || !spotifyData.playing) {
    return (
      <WidgetContainer size="1x2" delay={delay}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-cream-100">Now Playing</h3>
          <Pause className="w-4 h-4 text-coffee-300" />
        </div>
        
        <div className="flex flex-col items-center justify-center h-32 text-center">
          <Music className="w-12 h-12 text-coffee-400 mb-3 opacity-50" />
          <div className="text-sm font-medium text-coffee-300">Offline</div>
          <div className="text-xs text-coffee-400 mt-1">No music playing</div>
        </div>
      </WidgetContainer>
    );
  }

  const progressPercentage = (localProgress / spotifyData.duration_ms) * 100;
  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <WidgetContainer size="1x2" delay={delay}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-cream-100">Now Playing</h3>
        {spotifyData.playing ? (
          <div className="flex items-center gap-2">
            <span className="live-glow w-2 h-2 bg-green-500 rounded-full"></span>
            <Music className="w-4 h-4 text-green-500" />
          </div>
        ) : (
          <Pause className="w-4 h-4 text-coffee-300" />
        )}
      </div>

      <div className="flex gap-4 mb-4">
        {/* Album Art */}
        <div className="relative flex-shrink-0">
          <img
            src={spotifyData.album_art}
            alt={spotifyData.album}
            className="w-20 h-20 rounded-lg shadow-lg"
          />
          {spotifyData.playing && (
            <div className="absolute inset-0 rounded-lg bg-black/20 flex items-center justify-center">
              <Play className="w-6 h-6 text-white animate-pulse" />
            </div>
          )}
        </div>

        {/* Track Info */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-cream-100 truncate">
            {spotifyData.track_name}
          </div>
          <div className="text-xs text-coffee-200 truncate">
            {spotifyData.artist}
          </div>
          <div className="text-xs text-coffee-300 truncate mt-1">
            {spotifyData.album}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${progressPercentage}%` }}
        ></div>
      </div>
      <div className="flex justify-between text-xs text-coffee-300 mt-1">
        <span>{formatTime(localProgress)}</span>
        <span>{formatTime(spotifyData.duration_ms)}</span>
      </div>
    </WidgetContainer>
  );
};

export default NowPlaying;
